import Decimal from "decimal.js";
import type {
  CashierPaymentMethod,
  SavedPaymentAccountSummary,
} from "@/api/cashier";

type ProfitConvertedAmountInput = {
  /** Selected NEW profit method (rate lives on `settings[code]`). */
  newMethod?: CashierPaymentMethod;
  /** Selected SAVED profit method (rate lives on `processing_currencies`). */
  savedMethod?: SavedPaymentAccountSummary;
  /** Receiving currency the profit is being converted into. */
  conversionCurrencyCode: string | null | undefined;
  /** Profit amount denominated in the trading currency. */
  amount: Decimal | null | undefined;
};

export function conversionMinimumInTradingCurrency(
  paymentMethod: CashierPaymentMethod | undefined,
  conversionCurrencyCode: string | null | undefined,
  minimumInConversionCurrency: Decimal | null | undefined
): Decimal | null {
  if (
    !paymentMethod ||
    !conversionCurrencyCode ||
    !minimumInConversionCurrency
  ) {
    return null;
  }
  const rateValue =
    paymentMethod.settings[conversionCurrencyCode]?.trading_currency
      ?.exchange_rate?.value;
  const rate = rateValue != null ? new Decimal(rateValue) : null;
  return rate && !rate.isZero() ? minimumInConversionCurrency.div(rate) : null;
}

export function getProfitConvertedAmount({
  newMethod,
  savedMethod,
  conversionCurrencyCode,
  amount,
}: ProfitConvertedAmountInput): number | null {
  if (!conversionCurrencyCode || !amount || amount.isZero()) {
    return null;
  }

  let rate: Decimal | null = null;
  if (newMethod) {
    const value =
      newMethod.settings[conversionCurrencyCode]?.trading_currency
        ?.exchange_rate?.value;
    rate = value != null ? new Decimal(value) : null;
  } else if (savedMethod) {
    const pc = savedMethod.processing_currencies.find(
      (c) => c.code === conversionCurrencyCode
    );
    rate = pc?.rate ? new Decimal(pc.rate) : null;
  }

  if (!rate || rate.isZero()) {
    return null;
  }
  return amount.mul(rate).toNumber();
}

export function convertToReceivingCurrency(
  amountInTradingCurrency: Decimal,
  conversionRate: Decimal | null | undefined,
  fromProcessingCurrencies: boolean
): Decimal | null {
  if (!conversionRate || conversionRate.isZero()) {
    return null;
  }
  return fromProcessingCurrencies
    ? amountInTradingCurrency.mul(conversionRate)
    : amountInTradingCurrency.div(conversionRate);
}

export function convertToTradingCurrency(
  amountInReceivingCurrency: Decimal,
  conversionRate: Decimal | null | undefined,
  fromProcessingCurrencies: boolean
): Decimal | null {
  if (!conversionRate || conversionRate.isZero()) {
    return null;
  }
  return fromProcessingCurrencies
    ? amountInReceivingCurrency.mul(conversionRate)
    : amountInReceivingCurrency.div(conversionRate);
}

export function isProfitBelowConversionMinimum(
  convertedAmount: number | null,
  minimum: Decimal | null | undefined
): boolean {
  if (minimum == null || convertedAmount == null) {
    return false;
  }
  const converted = new Decimal(convertedAmount);
  return converted.gt(0) && converted.lt(minimum);
}
